import { Checkbox, FormControlLabel, Stack } from "@mui/material";
import { useLSSearch } from "./LSSearch";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { useAccount } from "@tlc/ls2pac/components/Account";
import { FormTextField } from "@tlc/forms/FormTextField";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useLanguage } from "@tlc/ls2pac/components/Language";
import {
  SavedSearchesService,
  SaveSearchParams,
} from "../../account/searches/SavedSearches";
import { useToast } from "@tlc/ui/toast";
import { LoginClientManager } from "@tlc/ls2pac/app/ClientManagers/LoginClientManager";
import {
  isAdvancedSearchTerm,
  isReadingLevelSearch,
} from "../advanced/AdvancedSearchTerm";
import { useAnalytics } from "../../../../analytics/useGoogleAnalytics";
import { ListType } from "../../account/ListService";
import { FormDialog } from "@tlc/ui/dialog/FormDialog";
import { useDialog } from "@tlc/ui/dialog/useDialog";

interface SaveSearchDialogProps {
  open: boolean;
  onClose: () => void;
}

interface SaveSearchFormInput {
  name: string;
  isConfig: boolean;
}

const SaveSearchDialog = ({ open, onClose }: SaveSearchDialogProps) => {
  const {
    searchParams: { rawTerm, term, facetFilters, branchFilters, sort },
  } = useLSSearch();
  const { trackEvent } = useAnalytics();
  const { showToast } = useToast();
  const { isConfigAdmin } = useAccount();
  const { translate } = useLanguage();

  const defaults = useMemo(() => {
    return {
      name:
        isAdvancedSearchTerm(rawTerm) || isReadingLevelSearch(rawTerm)
          ? ""
          : term,
      isConfig: isConfigAdmin,
    };
  }, [rawTerm, term, isConfigAdmin]);

  const formMethods = useForm<SaveSearchFormInput>({
    defaultValues: defaults,
    mode: "onBlur",
  });
  const { handleSubmit, reset, control } = formMethods;

  // reset on open
  useEffect(() => {
    if (open) {
      reset(defaults);
    }
  }, [open, reset, defaults]);

  const onSubmit = async ({ isConfig, name }: SaveSearchFormInput) => {
    const readingLevelFilters = isReadingLevelSearch(rawTerm)
      ? rawTerm.readingLevelFilters
      : null;
    const payload: SaveSearchParams = {
      expression: term,
      facetFilters,
      branchFilters,
      readingLevelFilters,
      name,
      sortType: sort,
    };
    try {
      await SavedSearchesService.saveSearch(payload, isConfig);
      trackEvent("save_search", {
        list_type: isConfig ? ListType.Config : ListType.Personal,
        search_term: term,
        search_type: isAdvancedSearchTerm(rawTerm)
          ? "advanced"
          : isReadingLevelSearch(rawTerm)
          ? "reading_level"
          : "keyword",
      });
      showToast(translate("savedSearchSuccessMessage"), "success");
      reset();
      onClose();
    } catch (e: unknown) {
      showToast(translate("savedSearchErrorMessage"), "error");
      console.error(e);
    }
  };

  return (
    <FormDialog
      maxWidth="xs"
      open={open}
      onClose={onClose}
      headline={translate("saveSearch")}
      id="save-search-dialog"
      formId="save-search-form"
      confirmButtonText={translate("save")}
    >
      <FormProvider {...formMethods}>
        <form
          id="save-search-form"
          noValidate
          onSubmit={handleSubmit(onSubmit)}
        >
          <Stack gap={1} pt={2}>
            <FormTextField<SaveSearchFormInput>
              name={"name"}
              label={translate("searchName")}
              maxLength={255}
              required
            ></FormTextField>
            {isConfigAdmin && (
              <Controller<SaveSearchFormInput, "isConfig">
                name={"isConfig"}
                control={control}
                render={({ field: { onChange, value } }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={value}
                        onChange={(_, checked) => onChange(checked)}
                      />
                    }
                    label={translate("saveAsConfig")}
                  />
                )}
              />
            )}
          </Stack>
        </form>
      </FormProvider>
    </FormDialog>
  );
};

interface SaveSearchDialogContextType {
  showDialog: () => void;
}

const SaveSearchDialogContext = createContext<
  SaveSearchDialogContextType | undefined
>(undefined);

export const useSaveSearchDialog = (): SaveSearchDialogContextType => {
  const context = useContext(SaveSearchDialogContext);
  if (!context)
    throw new Error(
      "useSaveSearchDialog must be used within a SaveSearchDialogProvider",
    );
  return context;
};

export const SaveSearchDialogProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { open, closeDialog, openDialog } = useDialog();

  //   TODO return a Promise with the result of the dialog?
  const showDialog = useCallback(() => {
    // TODO replace promptIfNotLoggedIn this with a context/provider
    LoginClientManager.promptIfNotLoggedIn(() => {
      openDialog();
    });
  }, [openDialog]);

  return (
    <SaveSearchDialogContext.Provider value={{ showDialog }}>
      <SaveSearchDialog open={open} onClose={closeDialog}></SaveSearchDialog>
      {children}
    </SaveSearchDialogContext.Provider>
  );
};
